import type { RowAction } from '../../admin/shared';
import type { CheckinActionKey, CheckinRowVm } from './checkin-row.vm';

/**
 * Monta a lista de ações de linha da tela de Check-ins.
 *
 * Função pura: recebe os handlers e devolve `RowAction<CheckinRowVm>[]`. A
 * DECISÃO de disponibilidade não mora aqui — `vm.acoes` já resolveu presença,
 * CASL, impressora conectada e as preferências de visibilidade em
 * `checkin-row.vm.ts`. Repetir a regra na tabela é como as três cópias do
 * `permission` da tela antiga divergiram entre si; aqui `permission` só
 * consulta o viewmodel.
 */

/**
 * Rótulo de cada chave, um por `CheckinActionKey`. `Record` completo de
 * propósito: chave nova no VM quebra a compilação aqui em vez de sair sem
 * rótulo na tela.
 */
const ROTULOS: Record<CheckinActionKey, string> = {
  checkin: 'Check-in',
  desfazer: 'Desfazer check-in',
  reenviar: 'Reenviar ingresso',
  certificado: 'Gerar certificado',
  imprimirEtiqueta: 'Imprimir etiqueta',
  credenciamento: 'Credenciamento',
  camposExtras: 'Ver detalhes',
};

/**
 * Ordem estável do kebab. É a mesma da `PRIORIDADE` do VM (que define a ação
 * primária do card mobile), para o menu do desktop e o action sheet do mobile
 * listarem as ações na mesma sequência.
 */
const ORDEM: readonly CheckinActionKey[] = [
  'checkin',
  'certificado',
  'reenviar',
  'imprimirEtiqueta',
  'credenciamento',
  'camposExtras',
  'desfazer',
];

/** Ações destrutivas — renderizadas em vermelho pela `AdminTableComponent`. */
/**
 * Fixada na linha por padrão. Só o check-in: é a ação de quase toda linha numa
 * fila de portaria, e escondê-la no menu custa dois cliques por pessoa. O
 * usuário pode fixar outras nas configurações da tabela — a escolha dele vence
 * este padrão e fica salva junto com as preferências de coluna.
 */
const FIXADAS_POR_PADRAO = new Set<CheckinActionKey>(['checkin']);

const DESTRUTIVAS: ReadonlySet<CheckinActionKey> = new Set<CheckinActionKey>(['desfazer']);

/**
 * Só entram no array as chaves que receberam handler: uma ação sem quem a
 * execute seria um item de menu morto.
 */
export function construirAcoes(
  handlers: Partial<Record<CheckinActionKey, (row: CheckinRowVm) => void>>,
): RowAction<CheckinRowVm>[] {
  return ORDEM.flatMap((chave) => {
    const handler = handlers[chave];
    if (!handler) return [];
    return [
      {
        key: chave,
        label: ROTULOS[chave],
        danger: DESTRUTIVAS.has(chave),
        pinned: FIXADAS_POR_PADRAO.has(chave),
        permission: (row: CheckinRowVm) => row.acoes.includes(chave),
        action: handler,
      },
    ];
  });
}
